import i18next from 'i18next';
import moment, {type Moment} from 'moment-timezone';
import React, {type ReactNode} from 'react';

import {Text} from '@famly/mf_data-display_text';
import {Button} from '@famly/mf_input_button';
import {FormControl} from '@famly/mf_input_form-control';
import {MultiSelect, Select, type SelectOption} from '@famly/mf_input_select';
import {Box} from '@famly/mf_layout_box';
import {Stack} from '@famly/mf_layout_stack';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {CurrencyFormatterProvider} from 'web-app/react/bookings-finance/contexts/currency-formatter-context';
import Spinner from 'web-app/react/components/loading/spinner/spinner';
import Tooltip from 'web-app/react/components/tooltip/tooltip';
import {needsTranslations} from 'web-app/util/scoped-needs-translation';
import {toDefault} from 'web-app/util/to-default';

import {FallbackButtonToLegacyPlanEditor} from '../../components/fallback-button-to-legacy-plan-editor';
import {Fees} from '../../components/fees';
import {OptionAdornment} from '../../components/option-adornment';
import {PlanOverview} from '../../components/overview/plan-overview/plan-overview';
import PricingGroupSelect from '../../components/pricing-group-select';
import {Tuitions} from '../../components/tuitions';
import {AUTOMATIC_PRICING_GROUP_ID} from '../../constants';
import {formatPlanEndDate, formatPlanStartDate} from '../../helpers/generate-edit-plan-date-range-options';
import {isChronologicalLastPlan} from '../../helpers/helpers';
import type {Handlers} from '../../hooks/use-handlers';
import type {SelectOptions} from '../../hooks/use-select-options';
import * as Tracking from '../../tracking';
import type * as Types from '../../types';
import {PlanStartAndEndDateBanner} from '../components/plan-start-and-end-date-banner';
import {InvoiceSummary} from '../../components/overview/invoice-summary-v2/loader';

const ChildrenErrors = React.lazy(() => import('../../components/children-errors').then(toDefault('ChildrenErrors')));
const UnsupportedPlanNotice = React.lazy(() =>
    import('../../edit-plan/components/unsupported-plan-notice').then(toDefault('UnsupportedPlanNotice')),
);

type Result = {
    planStartAndEndDateBanner: ReactNode;
    tuitionSelect: ReactNode;
    pricingGroupSelect: ReactNode;
    discountSelect: ReactNode;
    feeSelect: ReactNode;
    billingFrequencySelect: ReactNode;
    summary: ReactNode;
    childrenErrors: ReactNode;
    deleteButton: ReactNode;
    fallbackButtonToLegacyPlanEditor: ReactNode;
    planOverview: ReactNode;
    unsupportedPlanNotice: ReactNode;
};

type Args = {
    state: Types.EditPlanState;
    handlers: Handlers & {handleLegacyEditPlanModalOpen: VoidFunction; handleDeletePlan: VoidFunction};
    permissions: {
        canOverridePrice: boolean;
        isAutomaticDecouplingEnabled: boolean;
    };
    initialStartDate: Types.InitialStartDate;
    firstUnbilledDate: Moment | null;
    selectOptions: SelectOptions & {
        planDateRangeOptions: {startDateOptions: Array<SelectOption>; endDateOptions: Array<SelectOption>};
    };
    initialBillingFrequency: Types.InitialBillingFrequency;
    billingFrequencyConfig: Types.BillingFrequency[];
    isPlanFullyBilled: boolean;
    planOverview: null | Types.PlanOverview;
    siteId: string;
};

export const useEditBillingPlanFormContent = ({
    state: {
        childrenState,
        tuitionsState,
        feesState,
        pricingGroupState: {selectedPricingGroup, hasAutomaticPricingGroup},
        discountState: {selectedDiscounts},
        billingFrequencyState: {selectedBillingFrequency},
        planDateRange,
        view,
        selectedPlanId,
        childrenErrors,
    },
    handlers: {
        handleTuitionsChange,
        appendTuitionField,
        removeTuitionField,
        toggleTuitionFieldAtIndex,
        handleFeesChange,
        appendFeeField,
        removeFeeField,
        toggleFeeFieldAtIndex,
        handleDiscountChange,
        handlePricingGroupChange,
        handleBillingFrequencyChange,
        handleModalToggle,
        handlePlanStartDateChange,
        handlePlanEndDateChange,
        handleRemoveChildrenWithError,
        handleLegacyEditPlanModalOpen,
        handleDeletePlan,
    },
    permissions: {canOverridePrice, isAutomaticDecouplingEnabled},
    selectOptions: {
        tuitionOptions,
        feeOptions,
        discountOptions,
        billingFrequencyOptions,
        pricingGroupOptions,
        planDateRangeOptions: {startDateOptions, endDateOptions},
    },
    initialStartDate: {type: initialStartDateType},
    initialBillingFrequency,
    isPlanFullyBilled,
    planOverview,
    siteId,
}: Args): Result => {
    const updatedPlan = React.useMemo(() => {
        if (!hasValue(selectedPlanId)) {
            return undefined;
        }
        const isPercent = (discount: Types.Discount): boolean =>
            discount.type === 'preset' || discount.customDiscountType === 'percentage';
        const discounts = [...selectedDiscounts]
            .sort((discount1, discount2) => discount1.ordering - discount2.ordering)
            .map(discount => ({
                name: discount.title,
                amount: discount.amount,
                percent: isPercent(discount),
            }));
        const isRuleGroup = hasValue(selectedPricingGroup) && selectedPricingGroup === AUTOMATIC_PRICING_GROUP_ID;
        const pricingGroupId = !isRuleGroup ? selectedPricingGroup : null;
        return {
            planId: selectedPlanId,
            range: {
                fromInclusive: moment(planDateRange.startDate),
                toInclusive: hasValue(planDateRange.endDate) ? moment(planDateRange.endDate) : null,
            },
            tuitions: tuitionsState.selectedTuitions.map(tuition => ({
                tuitionId: tuition.id,
                overridePrice: tuition.overriddenPrice ?? null,
            })),
            fees: feesState.selectedFees.map(fee => ({feeId: fee.id})),
            discounts,
            pricingGroupId,
            ruleGroupId: null,
            defaultRuleGroup: isRuleGroup,
        };
    }, [
        selectedPlanId,
        planDateRange,
        tuitionsState.selectedTuitions,
        feesState.selectedFees,
        selectedDiscounts,
        selectedPricingGroup,
    ]);
    const invoiceSummaryV2 = React.useMemo(
        () =>
            childrenState.selectedChildren.length === 1 ? (
                <InvoiceSummary
                    childId={childrenState.selectedChildren[0].id}
                    updatedPlan={updatedPlan}
                    siteId={siteId}
                />
            ) : null,
        [childrenState.selectedChildren, siteId, updatedPlan],
    );

    const isPlanStartAndEndDateBannerDisplayed = !hasValue(planDateRange);

    const isDeleteDisabled = isPlanFullyBilled || initialStartDateType === 'current-plan-effective-date';

    const tooltipContent = (() => {
        if (isPlanFullyBilled) {
            return i18next.t('react.simplifiedUsBillingPlan.translations.json.deletePlan.tooltipFullyBilledPlan');
        }
        return i18next.t('react.simplifiedUsBillingPlan.translations.json.deletePlan.tooltip');
    })();

    const onClickFallbackToLegacyEditPlan = () => {
        Tracking.openGlobalPlanModalFromChillBill(isPlanFullyBilled ? 'fully billed button' : 'more options link');
        handleLegacyEditPlanModalOpen();
    };

    const isLastPlan = React.useMemo(() => {
        return (
            hasValue(selectedPlanId) && isChronologicalLastPlan(childrenState.selectedChildren[0].plans, selectedPlanId)
        );
    }, [childrenState.selectedChildren, selectedPlanId]);

    return {
        planStartAndEndDateBanner: isPlanStartAndEndDateBannerDisplayed ? null : (
            <PlanStartAndEndDateBanner
                startDate={{
                    value: planDateRange.startDate,
                    formattedDate: formatPlanStartDate(initialBillingFrequency, planDateRange.startDate),
                    options: startDateOptions,
                    onChange: handlePlanStartDateChange,
                    isDisabled: isPlanFullyBilled,
                }}
                endDate={{
                    value: planDateRange.endDate,
                    formattedDate: planDateRange.endDate
                        ? formatPlanEndDate(initialBillingFrequency, planDateRange.endDate)
                        : null,
                    options: endDateOptions,
                    onChange: handlePlanEndDateChange,
                    isDisabled: !isLastPlan || isPlanFullyBilled,
                }}
            />
        ),
        tuitionSelect: (
            <Tuitions
                onChange={handleTuitionsChange}
                appendField={appendTuitionField}
                removeField={removeTuitionField}
                toggleField={toggleTuitionFieldAtIndex}
                tuitionsState={tuitionsState}
                tuitionOptions={tuitionOptions}
                isDisabled={(!hasAutomaticPricingGroup && !hasValue(selectedPricingGroup)) || isPlanFullyBilled}
                canOverridePrice={canOverridePrice}
                handleModalToggle={handleModalToggle}
            />
        ),
        pricingGroupSelect: (
            <PricingGroupSelect
                options={pricingGroupOptions}
                value={selectedPricingGroup}
                onChange={handlePricingGroupChange}
                isDisabled={isPlanFullyBilled}
            />
        ),
        discountSelect: (
            <FormControl fullWidth label={needsTranslations('SimplifiedUsBilling', 'Discount')}>
                <Stack alignItems="center">
                    <MultiSelect
                        fullWidth
                        onChange={handleDiscountChange}
                        options={discountOptions}
                        value={selectedDiscounts.map(discount => discount.id)}
                        getOptionAdornment={option => (
                            <OptionAdornment>{option.type === 'discount' && option.amount}</OptionAdornment>
                        )}
                        data-e2e-id="discounts-select"
                        disabled={isPlanFullyBilled}
                    />
                </Stack>
            </FormControl>
        ),
        feeSelect: (
            <Fees
                onChange={handleFeesChange}
                appendField={{
                    onClick: appendFeeField,
                    isDisabled: !hasAutomaticPricingGroup && !hasValue(selectedPricingGroup),
                }}
                removeField={removeFeeField}
                fields={feesState.inputFields}
                feeOptions={feeOptions}
                toggleField={toggleFeeFieldAtIndex}
                fieldCurrentlyOpen={feesState.fieldCurrentlyOpen}
                isDisabled={isPlanFullyBilled}
            />
        ),
        billingFrequencySelect: (
            <Box width="100%" mr={5}>
                <FormControl
                    fullWidth
                    label={
                        isAutomaticDecouplingEnabled
                            ? i18next.t('react.simplifiedUsBillingPlan.translations.json.billingFrequency')
                            : i18next.t('react.simplifiedUsBillingPlan.translations.json.automaticBilling')
                    }
                >
                    <Select
                        onChange={handleBillingFrequencyChange}
                        options={billingFrequencyOptions}
                        value={selectedBillingFrequency}
                        data-e2e-id="billing-frequency-select"
                        disabled={view === 'edit' || isPlanFullyBilled}
                    />
                </FormControl>
            </Box>
        ),
        summary: invoiceSummaryV2,
        childrenErrors: (
            <React.Suspense fallback={<Spinner centered margined />}>
                <ChildrenErrors
                    childrenErrors={childrenErrors}
                    selectedChildren={childrenState.selectedChildren}
                    handleRemoveChildrenWithError={handleRemoveChildrenWithError}
                />
            </React.Suspense>
        ),
        deleteButton: isDeleteDisabled ? (
            <Tooltip
                trigger={['hover']}
                placement="top"
                overlay={
                    <Box width={isPlanFullyBilled ? '275px' : '305px'}>
                        <Text variant="body-small">{tooltipContent}</Text>
                    </Box>
                }
            >
                <Box>
                    <Button
                        variant="critical"
                        aria-label={i18next.t('react.simplifiedUsBillingPlan.translations.json.deletePlan.delete')}
                        icon="delete"
                        onClick={handleDeletePlan}
                        isDisabled={isDeleteDisabled}
                        data-e2e-id="delete-button"
                    />
                </Box>
            </Tooltip>
        ) : (
            <Button
                variant="critical"
                aria-label={i18next.t('react.simplifiedUsBillingPlan.translations.json.deletePlan.delete')}
                icon="delete"
                onClick={handleDeletePlan}
                data-e2e-id="delete-button"
            />
        ),
        fallbackButtonToLegacyPlanEditor: (
            <FallbackButtonToLegacyPlanEditor
                type={isPlanFullyBilled ? 'button' : 'link'}
                onClick={onClickFallbackToLegacyEditPlan}
            />
        ),
        planOverview: planOverview ? (
            <CurrencyFormatterProvider siteSetId={siteId}>
                <PlanOverview planItems={planOverview.plans} />
            </CurrencyFormatterProvider>
        ) : null,
        unsupportedPlanNotice: (
            <React.Suspense fallback={<Spinner centered margined />}>
                <UnsupportedPlanNotice onGoToOldPlanEditor={onClickFallbackToLegacyEditPlan} />
            </React.Suspense>
        ),
    };
};
